import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import useSWRInfinite from 'swr/infinite';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';
import { Image } from '@/components';
import {
    Combobox,
    type ComboboxOption,
    type ComboboxOptionRenderMeta,
} from '@/components/Combobox/Combobox';
import {
    PopoverPositioner,
    PopoverRoot,
    PopoverTrigger,
} from '@/components/Popover';
import { cn } from '@/lib/utils';
import type { MergeCandidate } from './types';

const SEARCH_PAGE_SIZE = 20;

interface SearchPage {
    items: MergeCandidate[];
    total: number;
}

type SearchPageKey = readonly [string, string, string, number];

export interface TargetPickerLabels {
    searchPlaceholder: string;
    empty: string;
    select: string;
    unknown: string;
    clear: string;
}

interface TargetIdentityPickerProps {
    sourceId: string;
    selectedId: string | null;
    onSelect: (id: string, item: MergeCandidate) => void;
    onClear: () => void;
    showSuggestions: boolean;
    sectionLabel: string;
    swrKey: string;
    fallbackIcon: ReactNode;
    labels: TargetPickerLabels;
    fetchSuggestions: (sourceId: string) => Promise<MergeCandidate[]>;
    fetchTargets: (params: {
        search: string;
        sourceId: string;
        page: number;
        size: number;
    }) => Promise<SearchPage>;
    renderRow: (
        item: MergeCandidate,
        meta: { isSelected: boolean; label: string },
    ) => ReactNode;
}

export function TargetIdentityPicker({
    sourceId,
    selectedId,
    onSelect,
    onClear,
    showSuggestions,
    sectionLabel,
    swrKey,
    fallbackIcon,
    labels,
    fetchSuggestions,
    fetchTargets,
    renderRow,
}: TargetIdentityPickerProps) {
    const [open, setOpen] = useState(false);
    const [selectedData, setSelectedData] = useState<MergeCandidate | null>(
        null,
    );
    const [suggestions, setSuggestions] = useState<MergeCandidate[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

    useEffect(() => {
        if (!showSuggestions || !sourceId) return;
        setIsSuggestionsLoading(true);
        fetchSuggestions(sourceId)
            .then((items) => setSuggestions(items))
            .catch(() => setSuggestions([]))
            .finally(() => setIsSuggestionsLoading(false));
    }, [showSuggestions, sourceId, fetchSuggestions]);

    useEffect(() => {
        if (selectedId === null) {
            setSelectedData(null);
        }
    }, [selectedId]);

    const {
        data: searchPages,
        isValidating: isSearchValidating,
        size: searchSize,
        setSize: setSearchSize,
    } = useSWRInfinite<
        SearchPage,
        unknown,
        (
            pageIndex: number,
            previousPageData: SearchPage | null,
        ) => SearchPageKey | null
    >(
        (pageIndex, previousPageData) => {
            if (!searchQuery) return null;
            if (previousPageData && previousPageData.items.length === 0) {
                return null;
            }
            return [swrKey, sourceId, searchQuery, pageIndex + 1] as const;
        },
        ([, currentSourceId, currentQuery, page]) =>
            fetchTargets({
                search: currentQuery,
                sourceId: currentSourceId,
                page,
                size: SEARCH_PAGE_SIZE,
            }),
        { revalidateFirstPage: false, revalidateOnMount: true },
    );

    const searchResults: MergeCandidate[] = useMemo(
        () => searchPages?.flatMap((page) => page.items) ?? [],
        [searchPages],
    );
    const searchTotal = searchPages?.[0]?.total ?? 0;
    const hasMoreSearch = searchResults.length < searchTotal;

    const handleSearch = useCallback(
        (query: string) => {
            setSearchQuery(query);
            void setSearchSize(1);
        },
        [setSearchSize],
    );

    const handleLoadMore = useCallback(() => {
        if (!searchQuery || isSearchValidating || !hasMoreSearch) return;
        void setSearchSize(searchSize + 1);
    }, [
        searchQuery,
        isSearchValidating,
        hasMoreSearch,
        searchSize,
        setSearchSize,
    ]);

    const activeItems: MergeCandidate[] = searchQuery
        ? searchResults
        : showSuggestions
          ? suggestions
          : [];

    const isLoading = searchQuery ? isSearchValidating : isSuggestionsLoading;

    const dataById = useMemo(
        () => Object.fromEntries(activeItems.map((item) => [item.id, item])),
        [activeItems],
    );

    const options: ComboboxOption[] = activeItems.map((item) => ({
        value: item.id,
        label: item.name ?? labels.unknown,
    }));

    const handleToggle = useCallback(
        (id: string) => {
            if (id === selectedId) {
                onClear();
                setSelectedData(null);
                setOpen(false);
                return;
            }
            const data = dataById[id];
            if (data) {
                onSelect(id, data);
                setSelectedData(data);
                setOpen(false);
            }
        },
        [selectedId, dataById, onSelect, onClear],
    );

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
        if (newOpen) {
            setSearchQuery('');
            void setSearchSize(1);
        }
    };

    const renderOption = useCallback(
        (option: ComboboxOption, { isSelected }: ComboboxOptionRenderMeta) => {
            const data = dataById[option.value];
            if (!data) return null;
            return renderRow(data, { isSelected, label: option.label });
        },
        [dataById, renderRow],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg bg-grey-600 px-3 py-3">
            <span className="text-sm text-grey-300">{sectionLabel}</span>
            <PopoverRoot open={open} onOpenChange={handleOpenChange}>
                <div className="relative">
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="flex w-full items-center gap-3 rounded-md bg-grey-700 px-3 py-2 text-left outline-none transition-colors hover:bg-grey-650"
                            style={{
                                border: '2px solid rgba(255,255,255,0.08)',
                                boxShadow:
                                    '0 1px 2px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.15)',
                            }}
                        >
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-grey-600">
                                {selectedData?.imageUrl ? (
                                    <Image
                                        src={selectedData.imageUrl}
                                        alt={
                                            selectedData.name ?? labels.unknown
                                        }
                                        auth
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    fallbackIcon
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                {selectedData ? (
                                    <p className="truncate text-sm-medium text-white-100">
                                        {selectedData.name ?? labels.unknown}
                                    </p>
                                ) : (
                                    <p className="text-sm text-grey-400">
                                        {labels.select}
                                    </p>
                                )}
                            </div>
                            {!selectedData && (
                                <CaretDownIcon
                                    className={cn(
                                        'shrink-0 text-grey-400 transition-transform duration-150',
                                        open && 'rotate-180',
                                    )}
                                    size={16}
                                />
                            )}
                        </button>
                    </PopoverTrigger>
                    {selectedData && (
                        <button
                            type="button"
                            aria-label={labels.clear}
                            className="absolute right-3 top-1/2 -translate-y-1/2 shrink-0 rounded p-0.5 text-grey-400 outline-none hover:text-white-100"
                            onClick={() => {
                                onClear();
                                setSelectedData(null);
                            }}
                        >
                            <XIcon size={14} />
                        </button>
                    )}
                </div>
                <PopoverPositioner
                    align="start"
                    sideOffset={4}
                    className="rounded-[10px] bg-grey-600 border border-grey-400 shadow-[0px_8px_32px_0px_rgba(0,0,0,0.5)]"
                    style={{
                        width: 'var(--radix-popover-trigger-width)',
                    }}
                >
                    <Combobox
                        options={options}
                        selected={selectedId ? [selectedId] : []}
                        onToggle={handleToggle}
                        onSearch={handleSearch}
                        onLoadMore={searchQuery ? handleLoadMore : undefined}
                        hasMore={Boolean(searchQuery) && hasMoreSearch}
                        isLoading={isLoading}
                        searchPlaceholder={labels.searchPlaceholder}
                        emptyText={searchQuery ? labels.empty : undefined}
                        renderOption={renderOption}
                        className="w-full border-0 bg-transparent shadow-none"
                        listClassName="scrollbar-grey-400"
                    />
                </PopoverPositioner>
            </PopoverRoot>
        </div>
    );
}
